import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import useRevenueCat from "./useRevenueCat";
import useSubscriptionAPIClient from "../../api/subscription/hooks/useSubscriptionAPIClient";
import { dispatch } from "../../redux/store";
import { setUserSubscription } from "../../redux/actions/user";

/**
 * Reusable subscribe hook.
 * On mobile (Capacitor), triggers RevenueCat purchase flow directly.
 * On web, navigates to the /subscribe page.
 *
 * @param {object} options
 * @param {function} [options.onSuccess] - Called after a successful mobile purchase
 * @param {function} [options.onCancel]  - Called if the user cancels the mobile purchase
 * @returns {{ subscribe: () => Promise<void>, subscribing: boolean }}
 */
const useSubscribe = ({ onSuccess, onCancel } = {}) => {
  const navigate = useNavigate();
  const isMobile = Capacitor.isNativePlatform();
  const { purchaseMonthlySubscription } = useRevenueCat();
  const { getSubscriptionStatus } = useSubscriptionAPIClient();
  const [subscribing, setSubscribing] = useState(false);

  const subscribe = useCallback(async () => {
    if (isMobile) {
      setSubscribing(true);
      try {
        const result = await purchaseMonthlySubscription();
        if (result?.success) {
          // Optimistically update subscription state immediately so the UI
          // reflects the change before the backend webhook is processed
          dispatch(
            setUserSubscription({ plan: "member", status: "active" }),
          );

          // Then verify with backend (webhook may not have arrived yet)
          try {
            const sub = await getSubscriptionStatus();
            dispatch(setUserSubscription(sub));
          } catch (error) {
            // Keep optimistic state — backend will sync on next app load
            console.error(
              "Failed to fetch subscription status after purchase:",
              error,
            );
          }
          onSuccess?.();
          return { success: true };
        } else {
          onCancel?.();
          return { success: false, cancelled: result?.cancelled };
        }
      } catch (error) {
        console.error("Purchase flow error:", error);
        onCancel?.();
        return { success: false };
      } finally {
        setSubscribing(false);
      }
    } else {
      navigate("/subscribe");
      return { navigated: true };
    }
  }, [
    isMobile,
    purchaseMonthlySubscription,
    getSubscriptionStatus,
    navigate,
    onSuccess,
    onCancel,
  ]);

  return { subscribe, subscribing };
};

export default useSubscribe;



